import { StageCoachReviewInfo, StageQuestionInfo } from '@/features/centerReview';
import React, { useState } from 'react';
import Modal from '@/components/modal/Modal';
import { overlay } from 'overlay-kit';
import SvgIcon from '@/components/SvgIcon';
import { colors } from '@/lib/colors';
import { DemiLightText, MediumText, RegularText } from '@/lib/Text';
import { Col, Row, truncateString } from '@/lib/utils';
import { CustomAnswerItem } from './ModalAllReviewContent';

type Props = {
    label: string;
    stageData: StageCoachReviewInfo;
};

function ModalCoachReviewContent({ label, stageData }: Props) {
    const questionInfos = stageData.stageQuestionInfos ?? [];

    return (
        <Col className="flex w-full overflow-y-auto">
            {questionInfos.map((question, index) => (
                <QuestionSection key={question.questionCode || index} label={label} question={question} />
            ))}
        </Col>
    );
}

export default ModalCoachReviewContent;

type QuestionSectionProps = {
    label: string;
    question: StageQuestionInfo;
};

function QuestionSection({ label, question }: QuestionSectionProps) {
    const [isExpanded, setIsExpanded] = useState(false);

    const positiveRate = question.questionPositiveAnswerRate ?? 0;
    const negativeRate = question.questionNegativeAnswerRate ?? 0;
    const positiveCount = question.questionPositiveAnswerCount ?? 0;
    const negativeCount = question.questionNegativeAnswerCount ?? 0;

    const allPositiveAnswers = question.questionPositiveAnswerRankings ?? [];
    const allNegativeAnswers = question.questionNegativeAnswerRankings ?? [];

    const positiveAnswers = isExpanded ? allPositiveAnswers : allPositiveAnswers.slice(0, 3);
    const negativeAnswers = isExpanded ? allNegativeAnswers : allNegativeAnswers.slice(0, 3);

    const maxPositive = Math.max(...allPositiveAnswers.map((answer) => answer.questionAnswerCount), 0);
    const maxNegative = Math.max(...allNegativeAnswers.map((answer) => answer.questionAnswerCount), 0);

    const hasMorePositive = allPositiveAnswers.length > 3;
    const hasMoreNegative = allNegativeAnswers.length > 3;
    const hasMore = hasMorePositive || hasMoreNegative;

    const customCount = question.questionCustomAnswerCount ?? 0;
    const openCustomOpinions = () => {
        const customAnswers = (question.questionCustomAnswers ?? []).sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        // 직접 남긴 의견 모달 열기
        overlay.open(({ unmount }) => (
            <Modal onClose={unmount} isCloseButton borderRadius={37}>
                <Col className="py-[36px] px-[26px] w-[900px] h-[700px]">
                    <Row className="items-center mb-[28px]">
                        <SvgIcon name="SvgPencil" size={19} fill={'#191F28'} className="mr-[8px]" />
                        <div className="relative inline-block">
                            <MediumText
                                fontSize={20}
                                lineHeight={28}
                                color={'#191F28'}
                                letterSpacing={-0.7}
                                className="relative z-50"
                            >
                                {label}, {question.questionSubject}
                            </MediumText>
                            <Col className={` h-[4px] w-full absolute bottom-[3px] left-0 z-10 bg-[#CEDEFF] `} />
                        </div>
                        <DemiLightText
                            fontSize={20}
                            lineHeight={30}
                            color={'#191F28'}
                            className="mr-[6px]"
                            letterSpacing={-0.7}
                        >
                            에 대해 직접 남긴 의견
                        </DemiLightText>
                        <MediumText fontSize={20} lineHeight={28} color={'#191F28'} letterSpacing={-0.4}>
                            {customCount}
                        </MediumText>
                    </Row>
                    <div className="grid grid-cols-2 gap-x-[36px] gap-y-[28px] overflow-auto">
                        {customAnswers.map((customAnswer, index) => (
                            <CustomAnswerItem key={`${customAnswer.memberName}-${index}`} customAnswer={customAnswer} />
                        ))}
                    </div>
                </Col>
            </Modal>
        ));
    };

    return (
        <Col className="mb-[27px]">
            {/* 수업 경험, 직접남긴 의견 */}
            <Row className="items-center  mb-[32px]">
                <Row className="items-center h-[38px]  px-[24px] bg-[#3D4244] rounded-[9px] mr-[11px]">
                    <RegularText
                        fontSize={16}
                        lineHeight={21}
                        color={colors.white}
                        letterSpacing={-0.7}
                        className="mr-[8px]"
                    >
                        {truncateString(question.questionSubject, 12)}
                    </RegularText>
                    <SvgIcon name="SvgStar" size={9} fill={colors.white} className="mr-[4px]" />
                    <MediumText fontSize={16} lineHeight={21} color={colors.white} letterSpacing={-0.7}>
                        {question.questionAverageRating.toFixed(1)}
                    </MediumText>
                </Row>
                <button
                    type="button"
                    onClick={customCount > 0 ? openCustomOpinions : undefined}
                    disabled={customCount === 0}
                    className={`flex items-center h-[38px] px-[10px] bg-[#F1F2F4] rounded-[9px] transition-colors border-[0.5px] border-solid border-[#E8E8E8] ${
                        customCount > 0 ? 'hover:bg-[#E6EAEE]' : 'cursor-default opacity-60'
                    }`}
                >
                    <RegularText fontSize={14} lineHeight={20} color={colors.Text_1} letterSpacing={-0.3}>
                        직접 남긴 의견 {customCount}
                    </RegularText>
                </button>
            </Row>

            {/* 좋았어요, 싫었어요 */}
            <div className="flex gap-[32px]">
                <Col className="flex-1">
                    <Row className="items-center mb-[12px]">
                        <SvgIcon name="SvgReviewLike" fill="#00A793" size={16} className="mr-[8px]" />
                        <RegularText
                            fontSize={16}
                            lineHeight={21}
                            color="#191F28"
                            className="mr-[4px]"
                            letterSpacing={-0.7}
                        >
                            좋았어요
                        </RegularText>
                        <MediumText
                            fontSize={16}
                            lineHeight={21}
                            color="#00A793"
                            className="mr-[3px]"
                            letterSpacing={-0.7}
                        >
                            {Math.round(positiveRate)}%
                        </MediumText>
                        <DemiLightText fontSize={13} lineHeight={18} color={colors.Text_1} letterSpacing={-0.3}>
                            ({positiveCount}회)
                        </DemiLightText>
                    </Row>
                    <Col className="gap-[12px]">
                        {positiveAnswers.length > 0 ? (
                            <>
                                {positiveAnswers.map((answer) => {
                                    const rate = maxPositive
                                        ? Math.round((answer.questionAnswerCount / maxPositive) * 100)
                                        : 0;
                                    return (
                                        <Row
                                            key={answer.questionAnswerCode}
                                            className="flex items-center justify-between px-[14px] h-[44px] rounded-[9px] border-solid border-[0.5px] border-[#E8E8E8]"
                                            style={{
                                                background: `linear-gradient(to right, #E8F9F3 ${rate}%, #F9FAFB ${rate}%)`,
                                            }}
                                        >
                                            <RegularText
                                                fontSize={15}
                                                lineHeight={21}
                                                color="#12A25D"
                                                letterSpacing={-0.7}
                                            >
                                                {answer.questionAnswerContent}
                                            </RegularText>
                                            <RegularText
                                                fontSize={15}
                                                lineHeight={21}
                                                color="#12A25D"
                                                letterSpacing={-0.7}
                                            >
                                                {answer.questionAnswerCount}
                                            </RegularText>
                                        </Row>
                                    );
                                })}
                                {hasMore && (
                                    <button
                                        onClick={() => setIsExpanded(!isExpanded)}
                                        className="flex items-center justify-center w-full mt-[8px]"
                                    >
                                        <div className="flex items-center justify-center w-[30px] h-[30px] rounded-full border border-[#E6E8EA] hover:bg-[#e8e8e8] transition-colors bg-[#F9FAFB] border-solid">
                                            <SvgIcon
                                                name={isExpanded ? 'SvgUpStrokeArrow' : 'SvgDownStrokeArrow'}
                                                size={12}
                                                fill={colors.trans}
                                                stroke="#3D4244"
                                                strokeWidth={1.25}
                                            />
                                        </div>
                                    </button>
                                )}
                            </>
                        ) : (
                            <div className="flex items-center justify-center py-[16px]">
                                <RegularText fontSize={12} lineHeight={18} color="#C5C6C9">
                                    등록된 의견이 없어요
                                </RegularText>
                            </div>
                        )}
                    </Col>
                </Col>

                <Col className="flex-1">
                    <Row className="items-center mb-[12px]">
                        <SvgIcon name="SvgReviewBad" fill="#FF7173" size={15} className="mr-[6px]" />
                        <RegularText
                            fontSize={16}
                            lineHeight={21}
                            color="#191F28"
                            className="mr-[4px]"
                            letterSpacing={-0.7}
                        >
                            아쉬웠어요
                        </RegularText>
                        <MediumText
                            fontSize={16}
                            lineHeight={21}
                            color="#FF7173"
                            className="mr-[3px]"
                            letterSpacing={-0.7}
                        >
                            {Math.round(negativeRate)}%
                        </MediumText>
                        <DemiLightText fontSize={13} lineHeight={18} color={colors.Text_1} letterSpacing={-0.3}>
                            ({negativeCount}회)
                        </DemiLightText>
                    </Row>
                    <Col className="gap-[12px]">
                        {negativeAnswers.length > 0 ? (
                            <>
                                {negativeAnswers.map((answer) => {
                                    const rate = maxNegative
                                        ? Math.round((answer.questionAnswerCount / maxNegative) * 100)
                                        : 0;
                                    return (
                                        <Row
                                            key={answer.questionAnswerCode}
                                            className="flex items-center h-[44px] justify-between px-[12px] py-[8px] rounded-[9px] border-solid border-[0.5px] border-[#E8E8E8]"
                                            style={{
                                                background: `linear-gradient(to right, #FFF0F0 ${rate}%, #F9FAFB ${rate}%)`,
                                            }}
                                        >
                                            <RegularText
                                                fontSize={15}
                                                lineHeight={21}
                                                color="#D4354D"
                                                letterSpacing={-0.7}
                                            >
                                                {answer.questionAnswerContent}
                                            </RegularText>
                                            <RegularText
                                                fontSize={15}
                                                lineHeight={21}
                                                color="#D4354D"
                                                letterSpacing={-0.7}
                                            >
                                                {answer.questionAnswerCount}
                                            </RegularText>
                                        </Row>
                                    );
                                })}
                                {hasMore && (
                                    <button
                                        onClick={() => setIsExpanded(!isExpanded)}
                                        className="flex items-center justify-center w-full mt-[8px]"
                                    >
                                        <div className="flex items-center justify-center w-[30px] h-[30px] rounded-full border border-[#E6E8EA] hover:bg-[#e8e8e8] transition-colors bg-[#F9FAFB] border-solid">
                                            <SvgIcon
                                                name={isExpanded ? 'SvgUpStrokeArrow' : 'SvgDownStrokeArrow'}
                                                size={12}
                                                fill={colors.trans}
                                                stroke="#3D4244"
                                                strokeWidth={1.25}
                                            />
                                        </div>
                                    </button>
                                )}
                            </>
                        ) : (
                            <div className="flex items-center justify-center py-[16px]">
                                <RegularText fontSize={12} lineHeight={18} color="#C5C6C9">
                                    등록된 의견이 없어요
                                </RegularText>
                            </div>
                        )}
                    </Col>
                </Col>
            </div>

            <Col className="flex w-full h-[1px] bg-[#E6E8EA] mt-[27px]" />
        </Col>
    );
}
